import React from "react";
import { createPortal } from "./windowManager/browserFenster/fensterPortal";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "./risikoanalyseStep/tokens";
import {
  FELD_LABELS,
  FELD_REIHENFOLGE,
  firmendatenBefundFuerUrl,
  firmendatenFuerAnlage,
  firmendatenPruefeKandidaten,
  logoVorschlagHolen,
  type AnlageEingabe,
  type AnlageErgebnis,
  type BefundDaten,
  type BefundFelder,
  type SuchKandidat,
} from "../services/firmendatenService";
import { vorausgewaehlteFelder, type VorhandeneWerte } from "./firmendatenVorauswahl";
import { useInlineWertEingabeStyles } from "./inlineWertEingabe";
import { alleDokumente } from "./windowManager/browserFenster/fensterRegister";

/**
 * Optik: kvp-rd („Refined") wie die übrigen Dialoge der Anlage — Aubergine/Gold,
 * Hanken Grotesk, eigene Bedienelemente. Vorher kamen Fenster, Knöpfe, Kästchen
 * und Verweise aus Fluent UI und fielen aus dem Bild.
 *
 * Der Dialog wird nach `document.body` portaliert (er hängt in der Anlage
 * innerhalb eines Fensters) und hat EINEN Scrollbereich: Trefferlisten und
 * Feldauswahl werden lang, Kopf und Fußzeile müssen stehen bleiben.
 */
const useStyles = makeStyles({
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 100002,
    backgroundColor: "rgba(20,8,20,0.34)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: REFINED.fontUI,
  },
  surface: {
    width: "min(680px, 100%)",
    maxHeight: "min(86vh, 860px)",
    display: "flex",
    flexDirection: "column",
    backgroundColor: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: "14px",
    boxShadow: REFINED.cardShadow,
    overflow: "hidden",
    color: REFINED.ink,
  },
  kopf: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    padding: "15px 18px",
    borderBottom: `2px solid ${REFINED.goldLine}`,
  },
  titel: { margin: 0, fontSize: `${REFINED.fs.lg}px`, fontWeight: 800 },
  /** Der EINE Scrollbereich — Kopf und Fußzeile bleiben sichtbar. */
  koerper: {
    padding: "16px 18px",
    overflowY: "auto",
    flexGrow: 1,
    fontSize: `${REFINED.fs.sm}px`,
    color: REFINED.ink2,
    lineHeight: 1.5,
  },
  fuss: {
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "10px",
    padding: "12px 18px",
    borderTop: `1px solid ${REFINED.line}`,
    backgroundColor: REFINED.fieldBg,
    flexWrap: "wrap",
  },
  btn: {
    appearance: "none",
    font: "inherit",
    fontSize: `${REFINED.fs.sm}px`,
    fontWeight: 700,
    padding: "8px 16px",
    borderRadius: "10px",
    cursor: "pointer",
    backgroundColor: REFINED.white,
    border: `1px solid ${REFINED.aub100}`,
    color: REFINED.aub,
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  btnPrimaer: {
    backgroundColor: REFINED.aub,
    border: `1px solid ${REFINED.aub}`,
    color: REFINED.white,
  },
  btnKlein: { padding: "6px 12px", fontSize: "0.8rem" },
  zeile: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "10px",
    padding: "8px 0",
    borderBottom: `1px solid ${REFINED.line}`,
  },
  wert: { flexGrow: 1, minWidth: 0, wordBreak: "break-word", color: REFINED.ink },
  label: { color: REFINED.ink3, fontSize: "0.78rem" },
  vorhanden: { color: REFINED.ink3, fontSize: "0.78rem", fontStyle: "italic" },
  ersetzt: { color: REFINED.aub, fontSize: "0.78rem", fontWeight: 600 },
  quelle: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    fontSize: "0.8rem",
    marginBottom: "10px",
    color: REFINED.ink2,
  },
  link: { color: REFINED.aub, fontWeight: 600, textDecoration: "underline" },
  mehrfachZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    marginTop: "4px",
  },
  ergaenzen: {
    marginTop: "6px",
    appearance: "none",
    font: "inherit",
    fontSize: "0.78rem",
    fontWeight: 700,
    color: REFINED.aub,
    backgroundColor: "transparent",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textDecoration: "underline",
  },
  hinweis: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "8px",
    padding: "8px 10px",
    borderRadius: "6px",
    backgroundColor: REFINED.fieldBg,
    fontSize: "0.84rem",
    marginBottom: "10px",
  },
  warnung: { backgroundColor: REFINED.errBg, color: REFINED.err },
  kandidat: {
    padding: "10px",
    border: `1px solid ${REFINED.line}`,
    borderRadius: "6px",
    marginBottom: "8px",
  },
  kandidatKopf: { display: "flex", alignItems: "center", columnGap: "8px" },
  score: {
    fontSize: "0.76rem",
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: "10px",
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    whiteSpace: "nowrap",
  },
  klein: { fontSize: "0.78rem", color: REFINED.ink3, marginTop: "4px" },
  impressumName: { fontSize: "0.84rem", marginTop: "6px", color: REFINED.ink },
  mitte: { display: "flex", alignItems: "center", columnGap: "10px", padding: "18px 0" },
  /** Auswahl unter einem Feld, wenn das Impressum mehrere Werte nennt. */
  varianten: { display: "flex", flexWrap: "wrap", columnGap: "12px", rowGap: "4px", marginTop: "4px" },
  variante: { display: "inline-flex", alignItems: "center", columnGap: "5px", fontSize: "0.8rem", cursor: "pointer" },
  /** „Ist das das Logo der Firma?" — Vorschau links, Frage und Knöpfe rechts. */
  logoBlock: {
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    padding: "10px 12px",
    border: `1px solid ${REFINED.line}`,
    borderRadius: "10px",
    marginBottom: "12px",
    backgroundColor: REFINED.fieldBg,
  },
  logoBild: {
    width: "64px",
    height: "64px",
    objectFit: "contain",
    backgroundColor: REFINED.white,
    border: `1px solid ${REFINED.line}`,
    borderRadius: "8px",
    padding: "4px",
    boxSizing: "border-box",
    flexShrink: 0,
  },
  logoKnoepfe: { display: "flex", columnGap: "8px", marginTop: "8px", flexWrap: "wrap" },
  spinner: {
    width: "14px",
    height: "14px",
    borderRadius: "999px",
    border: `2px solid ${REFINED.aub100}`,
    borderTopColor: REFINED.aub,
    animationName: { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
    animationDuration: "0.8s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
  },
});

/**
 * Felder, von denen es mehrere geben darf.
 *
 * `ContactCommunication` führt beliebig viele Zeilen je Art; ein Impressum
 * nennt regelmäßig mehrere Nummern (Zentrale, Vertrieb) und Adressen (info@,
 * vertrieb@). Bis 08/2026 erzwang ein Radio genau eine davon — der Rest wurde
 * abgetippt oder ging verloren.
 */
export const MEHRFACH_FELDER = ['phone', 'email'] as const;
export type MehrfachFeld = (typeof MEHRFACH_FELDER)[number];

export const istMehrfach = (key: keyof BefundFelder): key is MehrfachFeld =>
  (MEHRFACH_FELDER as readonly string[]).includes(key);

/** Die zusätzlichen Werte je Mehrfachfeld — sie werden als eigene Zeilen angelegt. */
export type WeitereWerte = Record<MehrfachFeld, string[]>;

export interface FirmendatenBefuellenDialogProps {
  open: boolean;
  /** Aktuelle Eingaben der Maske — Grundlage der Suche. */
  eingabe: AnlageEingabe;
  /** Was in der Maske schon steht: diese Felder sind nie vorausgewählt. */
  vorhanden: VorhandeneWerte;
  /**
   * Welche belegten Felder überhaupt zum Überschreiben angeboten werden. In der
   * ANLAGE nur der Firmenname (getippt wird die Kurzform, im Impressum steht der
   * vollständige Rechtsträger) — alles andere ist frisch getippt und bleibt
   * stehen. In der AKTE entscheidet der Anwender je Feld, dort also alle.
   * Vorausgewählt wird ein belegtes Feld in keinem der beiden Fälle.
   */
  darfErsetzen?: (key: keyof BefundFelder) => boolean;
  onClose: () => void;
  /**
   * Übernimmt die gewählten Werte in die Maske.
   *
   * `felder` trägt je Feld genau einen Wert — den, der in das Maskenfeld bzw.
   * die Standardzeile gehört. `weitere` trägt die zusätzlichen Rufnummern und
   * Adressen, die als eigene Kommunikationszeilen angelegt werden. Darf ein
   * belegtes Feld nicht ersetzt werden, stehen dort ALLE gewählten Werte:
   * Die Standardzeile bleibt unberührt, die Angaben sind trotzdem in der Akte.
   */
  onUebernehmen: (felder: Partial<BefundFelder>, weitere?: WeitereWerte) => void;
  /**
   * Logo-Vorschlag („Ist das das Logo der Firma?"). Ohne diese Angabe fragt
   * der Dialog nicht nach dem Logo.
   *
   * Die Antwort wirkt SOFORT und unabhängig vom Übernehmen der Felder: In der
   * Akte speichert `onJa` das Logo direkt, in der Anlage merkt es sich das
   * Bild bis zum Speichern der Firma.
   */
  logo?: LogoFrage;
}

export interface LogoFrage {
  /**
   * Hat die Firma schon ein Logo? Dann fragt der Dialog nach dem „Ja"
   * zusätzlich, ob es ersetzt werden soll — ein gespeichertes Logo bleibt
   * sonst unangetastet.
   */
  vorhanden: boolean;
  /** Der Anwender hat das Logo bestätigt. `ersetzen`: ein vorhandenes ersetzen. */
  onJa: (datenUrl: string, ersetzen: boolean) => void | Promise<void>;
}

type LogoStand =
  | { art: "aus" }
  | { art: "laedt" }
  | { art: "keins"; hinweis: string | null }
  | { art: "frage"; bild: string; quelle: string | null }
  | { art: "ersetzen"; bild: string; quelle: string | null }
  | { art: "speichert"; bild: string; quelle: string | null }
  | { art: "uebernommen"; bild: string }
  | { art: "verworfen" }
  | { art: "fehler"; bild: string; quelle: string | null; meldung: string };

/** Hostname zur Anzeige der Herkunft. */
const hostVon = (url: string | null): string | null => {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

/**
 * „Daten befüllen" — in der Firmen-Anlage und in der Kundenakte.
 *
 * Zwei Wege, abhängig davon, was in der Maske steht: liegt eine Website oder
 * eine Firmen-E-Mail vor, wird deren Impressum direkt gelesen. Ist nur der Name
 * bekannt, wird gesucht — und die Treffer werden zur **Auswahl** vorgelegt, mit
 * Link zum Öffnen im neuen Tab. Es wird nie automatisch eine Seite als „richtig"
 * angenommen: welche Website der Firma gehört, entscheidet der Anwender.
 *
 * Übernommen wird nie still: vorausgewählt sind ausschließlich Felder, die im
 * Ziel noch LEER sind. Belegte Felder werden als belegt ausgewiesen und — soweit
 * `darfErsetzen` es zulässt — nur mit eigenhändig gesetztem Haken überschrieben.
 * In der Anlage gilt das allein für den Firmenname (getippt wird die Kurzform,
 * im Impressum steht der Rechtsträger), in der Akte für jedes Feld.
 */
export const FirmendatenBefuellenDialog: React.FC<FirmendatenBefuellenDialogProps> = ({
  open,
  eingabe,
  vorhanden,
  darfErsetzen = (key) => key === "name",
  onClose,
  onUebernehmen,
  logo,
}) => {
  const s = useStyles();
  const inline = useInlineWertEingabeStyles();
  const [logoStand, setLogoStand] = React.useState<LogoStand>({ art: "aus" });
  const [laden, setLaden] = React.useState(false);
  /** Zweite Stufe: Impressen werden geladen, die Liste steht schon. */
  const [pruefeLaeuft, setPruefeLaeuft] = React.useState(false);
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [ergebnis, setErgebnis] = React.useState<AnlageErgebnis | null>(null);
  const [befund, setBefund] = React.useState<BefundDaten | null>(null);
  const [quelle, setQuelle] = React.useState<string | null>(null);
  const [auswahl, setAuswahl] = React.useState<Set<string>>(new Set());
  const [hinweise, setHinweise] = React.useState<string[]>([]);
  /**
   * Bearbeitete Werte je Einzelfeld. Der Vorschlag ist ein Vorschlag: Ein
   * Impressum schreibt „Tel. 0241 / 123 456 - 0" oder hängt an den Firmennamen
   * einen Zusatz, und das gehört korrigierbar, BEVOR es in den Stammdaten
   * steht — nicht danach in der Maske.
   */
  const [werte, setWerte] = React.useState<Partial<Record<keyof BefundFelder, string>>>({});
  /**
   * Telefon und E-Mail sind Mehrfachfelder: Ein Impressum nennt regelmäßig
   * mehrere (Zentrale und Vertrieb, info@ und datenschutz@), und
   * `ContactCommunication` kann beliebig viele Zeilen je Art führen. Vorher
   * erzwang ein Radio genau eine — den Rest musste der Anwender abtippen.
   */
  const [mehrfach, setMehrfach] = React.useState<
    Partial<Record<MehrfachFeld, Array<{ wert: string; an: boolean }>>>
  >({});

  /**
   * Zweistufig, damit die Wartezeit nicht als Stillstand erlebt wird: Die Suche
   * antwortet in etwa einer halben Sekunde und liefert Adressen, Titel und
   * Textauszug — das wird sofort angezeigt und ist bereits anklickbar. Erst danach
   * werden die Impressen geladen (mehrere Sekunden) und Rechtsträger sowie
   * Punktzahl je Zeile nachgetragen.
   */
  const starten = React.useCallback(async () => {
    setLaden(true);
    setPruefeLaeuft(false);
    setFehler(null);
    setBefund(null);
    setErgebnis(null);
    try {
      const r = await firmendatenFuerAnlage({ ...eingabe, nurSuche: true });
      setErgebnis(r);
      setHinweise(r.hinweise);
      if (r.befund) {
        // Domain war bekannt — dann gibt es nichts nachzuladen.
        setBefund(r.befund);
        setQuelle(r.befund.impressumUrl ?? (r.domain ? `https://${r.domain}` : null));
        return;
      }
      if (r.modus !== "suche" || r.kandidaten.length === 0) return;

      setLaden(false);
      setPruefeLaeuft(true);
      const geprueft = await firmendatenPruefeKandidaten(
        r.kandidaten.slice(0, 5).map((k) => k.url),
        { name: eingabe.name, ort: eingabe.ort, plz: eingabe.plz },
      );
      // Prüfergebnisse in die vorhandenen Zeilen einweben — Titel und Textauszug
      // stammen aus der Suche und bleiben erhalten.
      setErgebnis((alt) => {
        if (!alt) return alt;
        const nachDomain = new Map(geprueft.kandidaten.map((k) => [k.domain, k]));
        const zusammen = alt.kandidaten.map((k) => {
          const g = nachDomain.get(k.domain);
          return g ? { ...k, ...g, titel: k.titel, snippet: k.snippet, gefundenVia: k.gefundenVia } : k;
        });
        zusammen.sort((a, b) => b.score - a.score);
        return { ...alt, kandidaten: zusammen };
      });
      if (geprueft.hinweise.length) setHinweise((h) => [...h, ...geprueft.hinweise]);
    } catch (e) {
      setFehler((e as Error).message);
    } finally {
      setLaden(false);
      setPruefeLaeuft(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(eingabe)]);

  React.useEffect(() => {
    if (open) void starten();
    else {
      setErgebnis(null);
      setBefund(null);
      setAuswahl(new Set());
      setHinweise([]);
      setFehler(null);
      setWerte({});
      setMehrfach({});
      setLogoStand({ art: "aus" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Logo-Vorschlag, sobald feststeht, welche Website zur Firma gehört.
   *
   * Früher lud die Akte das Favicon bei jedem Öffnen direkt von der
   * Kundendomain in den Browser — ungeprüft. Jetzt holt der Server das Bild
   * einmal, prüft es an den Bytes (kein SVG), und gespeichert wird nur, was
   * der Anwender hier bestätigt.
   */
  const logoGewuenscht = !!logo;
  const logoAdresse = befund
    ? befund.felder.website || quelle || eingabe.website || null
    : null;
  React.useEffect(() => {
    if (!open || !logoGewuenscht || !logoAdresse) return;
    let aktiv = true;
    setLogoStand({ art: "laedt" });
    logoVorschlagHolen(logoAdresse)
      .then((v) => {
        if (!aktiv) return;
        setLogoStand(
          v.logo
            ? { art: "frage", bild: v.logo, quelle: v.quelle }
            : { art: "keins", hinweis: v.hinweis },
        );
      })
      .catch((e) => {
        if (aktiv) setLogoStand({ art: "keins", hinweis: (e as Error).message });
      });
    return () => {
      aktiv = false;
    };
  }, [open, logoGewuenscht, logoAdresse]);

  const logoBestaetigen = async (bild: string, quelleBild: string | null, ersetzen: boolean) => {
    if (!logo) return;
    setLogoStand({ art: "speichert", bild, quelle: quelleBild });
    try {
      await logo.onJa(bild, ersetzen);
      setLogoStand({ art: "uebernommen", bild });
    } catch (e) {
      setLogoStand({ art: "fehler", bild, quelle: quelleBild, meldung: (e as Error).message });
    }
  };

  // ESC schließt — wie in den übrigen kvp-rd-Dialogen.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    alleDokumente.addEventListener("keydown", onKey);
    return () => alleDokumente.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /** Nur Felder anbieten, die einen Wert haben. */
  const anbietbar = React.useMemo(() => {
    if (!befund) return [];
    return FELD_REIHENFOLGE.filter((key) => {
      const wert = befund.felder[key];
      if (!wert || !String(wert).trim()) return false;
      // Ein Firmenname, der dem eingetippten entspricht, ist nichts zu übernehmen.
      if (key === "name") {
        return String(wert).trim().toLowerCase() !== eingabe.name.trim().toLowerCase();
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [befund, eingabe.name]);

  React.useEffect(() => {
    // Vorauswahl: alles, was im Ziel noch leer ist. Bereits Gefülltes bleibt
    // unberührt — auch der Firmenname, den nur ein bewusster Haken ersetzt.
    if (!befund) return;
    setAuswahl(vorausgewaehlteFelder(anbietbar, vorhanden));
    setWerte({});

    const listen: Partial<Record<MehrfachFeld, Array<{ wert: string; an: boolean }>>> = {};
    for (const key of MEHRFACH_FELDER) {
      const quelle = key === "email" ? befund.mails : befund.telefone;
      const gefunden = Array.from(
        new Set((quelle ?? []).map((v) => String(v).trim()).filter(Boolean)),
      );
      if (!gefunden.length && befund.felder[key]) gefunden.push(String(befund.felder[key]));
      // Angehakt ist nur der erste, und nur wenn im Ziel noch nichts steht.
      // Alle vorzuwählen wäre bequem und falsch: Ein Impressum führt von
      // Gesetzes wegen fremde Adressen (Datenschutzbeauftragter, Fotograf).
      const zielLeer = !String(vorhanden[key] ?? "").trim();
      listen[key] = gefunden.map((wert, i) => ({ wert, an: zielLeer && i === 0 }));
    }
    setMehrfach(listen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [befund, anbietbar]);

  /** Der Wert, der übernommen würde: der bearbeitete, sonst der Fund. */
  const wertFuer = (key: keyof BefundFelder): string =>
    String(werte[key] ?? befund?.felder[key] ?? "");

  const setzeMehrfach = (key: MehrfachFeld, i: number, patch: { wert?: string; an?: boolean }) =>
    setMehrfach((alt) => {
      const liste = [...(alt[key] ?? [])];
      liste[i] = { ...liste[i], ...patch };
      return { ...alt, [key]: liste };
    });

  const ergaenzeMehrfach = (key: MehrfachFeld) =>
    setMehrfach((alt) => ({ ...alt, [key]: [...(alt[key] ?? []), { wert: "", an: true }] }));

  /** Die angehakten, nicht leeren Werte eines Mehrfachfelds. */
  const gewaehlteWerte = (key: MehrfachFeld): string[] =>
    Array.from(
      new Set(
        (mehrfach[key] ?? [])
          .filter((z) => z.an && z.wert.trim())
          .map((z) => z.wert.trim()),
      ),
    );

  const kandidatWaehlen = async (k: SuchKandidat) => {
    setLaden(true);
    setFehler(null);
    try {
      const r = await firmendatenBefundFuerUrl(k.url, {
        name: eingabe.name,
        ort: eingabe.ort,
        plz: eingabe.plz,
      });
      setBefund(r.befund);
      setQuelle(r.befund?.impressumUrl ?? k.url);
      setHinweise(r.hinweise);
    } catch (e) {
      setFehler((e as Error).message);
    } finally {
      setLaden(false);
    }
  };

  const uebernehmen = () => {
    if (!befund) return;
    const felder: Partial<BefundFelder> = {};
    const weitere: WeitereWerte = { phone: [], email: [] };
    for (const key of anbietbar) {
      if (istMehrfach(key)) {
        const gewaehlt = gewaehlteWerte(key);
        if (!gewaehlt.length) continue;
        // Darf der vorhandene Eintrag nicht ersetzt werden, gehen ALLE
        // gewählten Werte als zusätzliche Zeilen mit — statt gar keiner. Die
        // Standardzeile bleibt dann unberührt, die Nummern sind trotzdem da.
        const belegt = !!String(vorhanden[key] ?? "").trim();
        if (belegt && !darfErsetzen(key)) {
          weitere[key] = gewaehlt;
          continue;
        }
        felder[key] = gewaehlt[0];
        weitere[key] = gewaehlt.slice(1);
        continue;
      }
      if (!auswahl.has(key)) continue;
      const wert = wertFuer(key).trim();
      if (wert) felder[key] = wert;
    }
    onUebernehmen(felder, weitere);
    onClose();
  };

  /**
   * Wie viele Angaben übernommen würden. Die Mehrfachfelder haben keinen
   * eigenen Haken mehr — sie zählen, sobald mindestens ein Wert angehakt ist.
   */
  const anzahlUebernahme = anbietbar.filter((key) =>
    istMehrfach(key) ? gewaehlteWerte(key).length > 0 : auswahl.has(key),
  ).length;

  const toggle = (key: keyof BefundFelder) => {
    setAuswahl((alt) => {
      const neu = new Set(alt);
      if (neu.has(key)) neu.delete(key);
      else neu.add(key);
      return neu;
    });
  };

  if (!open) return null;

  return createPortal(
    <div className={s.backdrop} role="presentation" onPointerDown={onClose}>
      <div
        className={s.surface}
        role="dialog"
        aria-modal="true"
        aria-label="Daten aus dem Impressum befüllen"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className={s.kopf}>
          <h3 className={s.titel}>Daten aus dem Impressum befüllen</h3>
        </div>

        <div className={s.koerper}>
          {hinweise.map((h, i) => (
            <div key={i} className={s.hinweis}>
              <span aria-hidden>⚠</span>
              <span>{h}</span>
            </div>
          ))}

          {fehler && (
            <div className={mergeClasses(s.hinweis, s.warnung)}>
              <span aria-hidden>⚠</span>
              <span>{fehler}</span>
            </div>
          )}

          {laden && (
            <div className={s.mitte}>
              <span className={s.spinner} aria-hidden />
              <span>{befund ? "Seite wird geprüft…" : "Suche läuft…"}</span>
            </div>
          )}

          {/* Zweite Stufe: die Liste steht schon und ist benutzbar, die Impressen
              werden noch geladen. */}
          {pruefeLaeuft && (
            <div className={s.quelle}>
              <span className={s.spinner} aria-hidden />
              <span>Impressen werden geprüft — die Treffer sind schon anklickbar.</span>
            </div>
          )}

          {/* Kandidatenauswahl — nur wenn gesucht wurde und noch keine Seite gewählt ist. */}
          {!laden && !befund && ergebnis?.modus === "suche" && (
            <>
              {ergebnis.kandidaten.length === 0 ? (
                // Bei gescheiterten Anfragen NICHT zum Ergänzen der Anschrift
                // auffordern — das führt in die Irre, wenn der Zugang abgelehnt
                // oder das Kontingent erschöpft war. Der Grund steht oben.
                ergebnis.anfragen.some((a) => a.fehler) ? (
                  <p>Die Websuche konnte nicht ausgeführt werden — siehe Hinweis oben.</p>
                ) : (
                  <p>
                    Keine Website gefunden. Ergänze Ort oder Postleitzahl, oder trage die
                    Website direkt ein.
                  </p>
                )
              ) : (
                <>
                  <p style={{ marginTop: 0 }}>
                    Welche Seite gehört zu <strong>{eingabe.name}</strong>? Vergleiche den
                    Rechtsträger aus dem Impressum — die Domain ist oft eine Abkürzung oder
                    lautet ganz anders. Zur Prüfung im neuen Tab öffnen.
                  </p>
                  {ergebnis.kandidaten.map((k) => (
                    <div key={k.domain} className={s.kandidat}>
                      <div className={s.kandidatKopf}>
                        <span className={s.score}>
                          {k.geprueft ? `${k.score} Punkte` : "wird geprüft…"}
                        </span>
                        <strong className={s.wert}>{k.domain}</strong>
                        <a className={s.link} href={k.url} target="_blank" rel="noreferrer">
                          Öffnen ↗
                        </a>
                      </div>
                      {/* Der Rechtsträger laut Impressum — die Angabe, an der der
                          Anwender wirklich entscheidet. */}
                      {k.firmenbezeichnung && (
                        <div className={s.impressumName}>
                          Impressum: <strong>{k.firmenbezeichnung}</strong>
                        </div>
                      )}
                      {k.geprueft && !k.firmenbezeichnung && (
                        <div className={s.klein}>Im Impressum war kein Rechtsträger zu erkennen</div>
                      )}
                      <div className={s.klein}>{k.titel}</div>
                      {k.begruendung.length > 0 && (
                        <div className={s.klein}>✓ {k.begruendung.join(" · ")}</div>
                      )}
                      {k.ablehnungsgrund && (
                        <div className={s.klein} style={{ color: REFINED.err }}>
                          ⚠ {k.ablehnungsgrund}
                        </div>
                      )}
                      <div style={{ marginTop: 8 }}>
                        <button
                          type="button"
                          className={mergeClasses(s.btn, s.btnPrimaer, s.btnKlein)}
                          onClick={() => void kandidatWaehlen(k)}
                        >
                          Diese Seite verwenden
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}

          {/* Befund mit Feldauswahl */}
          {!laden && befund && (
            <>
              {quelle && (
                <div className={s.quelle}>
                  <span aria-hidden>{befund.abgelehnt ? "⚠" : "✓"}</span>
                  <span>
                    Quelle:{" "}
                    <a className={s.link} href={quelle} target="_blank" rel="noreferrer">
                      {quelle} ↗
                    </a>
                    {befund.nurStartseite && " (Startseite — kein Impressum gefunden)"}
                  </span>
                </div>
              )}
              {/* Eingetippter Name gegen den Rechtsträger der Seite — der
                  Abgleich, den der Anwender vor dem Übernehmen sehen muss. */}
              {befund.firmenbezeichnung && (
                <div className={s.impressumName} style={{ marginBottom: 8 }}>
                  {eingabe.name.trim() && (
                    <>
                      Eingegeben: <strong>{eingabe.name}</strong>
                      <br />
                    </>
                  )}
                  Laut Impressum: <strong>{befund.firmenbezeichnung}</strong>
                </div>
              )}
              {befund.begruendung.length > 0 && (
                <div className={s.klein} style={{ marginBottom: 10 }}>
                  ✓ {befund.begruendung.join(" · ")}
                </div>
              )}
              {logo && logoStand.art !== "aus" && (
                <LogoVorschlagBlock
                  stand={logoStand}
                  vorhanden={logo.vorhanden}
                  s={s}
                  onJa={(bild, q) =>
                    logo.vorhanden
                      ? setLogoStand({ art: "ersetzen", bild, quelle: q })
                      : void logoBestaetigen(bild, q, false)
                  }
                  onErsetzen={(bild, q) => void logoBestaetigen(bild, q, true)}
                  onNein={() => setLogoStand({ art: "verworfen" })}
                />
              )}
              {befund.felder.vertretung && (
                <div className={s.klein} style={{ marginBottom: 10 }}>
                  Vertreten durch: {befund.felder.vertretung} — als Ansprechpartner anlegen, dieses
                  Feld gibt es in der Firmenmaske nicht.
                </div>
              )}

              {anbietbar.length === 0 ? (
                <p>Auf der Seite waren keine übernehmbaren Angaben zu finden.</p>
              ) : (
                anbietbar.map((key) => {
                  const schonGefuellt = !!String(vorhanden[key] ?? "").trim();
                  const ersetzbar = darfErsetzen(key);
                  const gesperrt = schonGefuellt && !ersetzbar;

                  // Telefon und E-Mail: mehrere Werte, jeder einzeln an- oder
                  // abwählbar und jeder bearbeitbar. Ein Impressum schreibt
                  // „Tel. 0241 / 123 456 - 0"; wie das in den Stammdaten stehen
                  // soll, entscheidet der Anwender hier — nicht hinterher.
                  if (istMehrfach(key)) {
                    const liste = mehrfach[key] ?? [];
                    return (
                      <div key={key} className={s.zeile}>
                        <div className={s.wert}>
                          <div className={s.label}>{FELD_LABELS[key]}</div>
                          {liste.map((z, i) => (
                            <div key={i} className={s.mehrfachZeile}>
                              <input
                                type="checkbox"
                                checked={z.an}
                                onChange={() => setzeMehrfach(key, i, { an: !z.an })}
                                aria-label={`${FELD_LABELS[key]}: ${z.wert || "neuer Eintrag"}`}
                                style={{ accentColor: REFINED.aub }}
                              />
                              <input
                                className={inline.eingabe}
                                value={z.wert}
                                aria-label={`${FELD_LABELS[key]} bearbeiten`}
                                onChange={(ev) => setzeMehrfach(key, i, { wert: ev.target.value })}
                              />
                            </div>
                          ))}
                          <button
                            type="button"
                            className={s.ergaenzen}
                            onClick={() => ergaenzeMehrfach(key)}
                          >
                            {key === "email" ? "+ weitere Adresse" : "+ weitere Nummer"}
                          </button>
                          {gesperrt && (
                            <div className={s.vorhanden}>
                              „{vorhanden[key]}" bleibt die Hauptangabe — Gewähltes kommt als
                              weitere Zeile dazu.
                            </div>
                          )}
                          {schonGefuellt && ersetzbar && (
                            <div className={s.ersetzt}>
                              der erste gewählte Wert ersetzt „{vorhanden[key]}", weitere kommen
                              als eigene Zeilen dazu
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={key} className={s.zeile}>
                      <input
                        type="checkbox"
                        checked={auswahl.has(key)}
                        onChange={() => toggle(key)}
                        disabled={gesperrt}
                        aria-label={FELD_LABELS[key]}
                        style={{ marginTop: 3, accentColor: REFINED.aub }}
                      />
                      <div className={s.wert}>
                        <div className={s.label}>{FELD_LABELS[key]}</div>
                        {/* Bearbeitbar statt nur anzeigbar: Der Fund ist ein
                            Vorschlag. Ein Impressum hängt an den Firmennamen
                            gern einen Zusatz, und das gehört korrigiert, BEVOR
                            es in den Stammdaten steht. */}
                        <input
                          className={inline.eingabe}
                          value={wertFuer(key)}
                          disabled={gesperrt}
                          aria-label={`${FELD_LABELS[key]} bearbeiten`}
                          onChange={(ev) =>
                            setWerte((alt) => ({ ...alt, [key]: ev.target.value }))
                          }
                        />
                        {gesperrt && (
                          <div className={s.vorhanden}>
                            bereits ausgefüllt („{vorhanden[key]}") — bleibt unverändert
                          </div>
                        )}
                        {schonGefuellt && ersetzbar && (
                          <div className={s.ersetzt}>
                            ersetzt den vorhandenen Eintrag („{vorhanden[key]}") — nur mit
                            gesetztem Haken
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>

        <div className={s.fuss}>
          <button type="button" className={s.btn} onClick={onClose}>
            Abbrechen
          </button>
          {befund && ergebnis?.modus === "suche" && (
            <button
              type="button"
              className={s.btn}
              onClick={() => { setBefund(null); setHinweise(ergebnis.hinweise); }}
            >
              Andere Seite wählen
            </button>
          )}
          <button
            type="button"
            className={mergeClasses(s.btn, s.btnPrimaer)}
            disabled={laden || !befund || anzahlUebernahme === 0}
            onClick={uebernehmen}
          >
            {anzahlUebernahme > 0 ? `${anzahlUebernahme} Angaben übernehmen` : "Übernehmen"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/** Der Vorschlagsblock „Ist das das Logo der Firma?". */
const LogoVorschlagBlock: React.FC<{
  stand: LogoStand;
  vorhanden: boolean;
  s: ReturnType<typeof useStyles>;
  onJa: (bild: string, quelle: string | null) => void;
  onErsetzen: (bild: string, quelle: string | null) => void;
  onNein: () => void;
}> = ({ stand, vorhanden, s, onJa, onErsetzen, onNein }) => {
  if (stand.art === "aus") return null;
  if (stand.art === "laedt") {
    return (
      <div className={s.quelle} data-testid="logo-vorschlag">
        <span className={s.spinner} aria-hidden />
        <span>Logo auf der Website wird gesucht…</span>
      </div>
    );
  }
  if (stand.art === "keins") {
    return (
      <div className={s.klein} style={{ marginBottom: 10 }} data-testid="logo-vorschlag">
        Kein Logo-Vorschlag{stand.hinweis ? ` — ${stand.hinweis}` : "."}
      </div>
    );
  }
  if (stand.art === "verworfen") {
    return (
      <div className={s.klein} style={{ marginBottom: 10 }} data-testid="logo-vorschlag">
        Logo-Vorschlag verworfen.
      </div>
    );
  }
  if (stand.art === "uebernommen") {
    return (
      <div className={s.logoBlock} data-testid="logo-vorschlag">
        <img className={s.logoBild} src={stand.bild} alt="Übernommenes Logo" />
        <div>✓ Logo übernommen.</div>
      </div>
    );
  }

  const herkunft = hostVon(stand.quelle);
  const beschaeftigt = stand.art === "speichert";
  return (
    <div className={s.logoBlock} data-testid="logo-vorschlag" role="group" aria-label="Logo-Vorschlag">
      <img className={s.logoBild} src={stand.bild} alt="Logo-Vorschlag von der Website" />
      <div>
        {stand.art === "ersetzen" ? (
          <>
            <strong>Die Firma hat schon ein Logo. Soll es durch dieses ersetzt werden?</strong>
            <div className={s.logoKnoepfe}>
              <button
                type="button"
                className={mergeClasses(s.btn, s.btnPrimaer, s.btnKlein)}
                onClick={() => onErsetzen(stand.bild, stand.quelle)}
              >
                Ja, ersetzen
              </button>
              <button type="button" className={mergeClasses(s.btn, s.btnKlein)} onClick={onNein}>
                Nein, vorhandenes behalten
              </button>
            </div>
          </>
        ) : (
          <>
            <strong>Ist das das Logo der Firma?</strong>
            {herkunft && <div className={s.klein}>gefunden auf {herkunft}</div>}
            {vorhanden && (
              <div className={s.klein}>Die Firma hat bereits ein Logo — vor dem Ersetzen wird nachgefragt.</div>
            )}
            {stand.art === "fehler" && (
              <div className={s.klein} style={{ color: REFINED.err }}>
                ⚠ {stand.meldung}
              </div>
            )}
            <div className={s.logoKnoepfe}>
              <button
                type="button"
                className={mergeClasses(s.btn, s.btnPrimaer, s.btnKlein)}
                disabled={beschaeftigt}
                onClick={() => onJa(stand.bild, stand.quelle)}
              >
                {beschaeftigt ? "Wird übernommen…" : "Ja"}
              </button>
              <button
                type="button"
                className={mergeClasses(s.btn, s.btnKlein)}
                disabled={beschaeftigt}
                onClick={onNein}
              >
                Nein
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FirmendatenBefuellenDialog;
